import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MdClose, MdCheckCircle } from 'react-icons/md';
import MoldurasGallery from '../MoldurasGallery';
import LogosGallery from '../LogosGallery';
import PersonalizacaoForm from './PersonalizacaoForm';
import api from '../../services/api';
import downloadSocialImage from '../../services/SocialImage/downloadSocialImage';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const Section = styled.div`
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f7f7f7;
  border-radius: 8px;
  border: 1px solid #ddd;
`;

const SectionTitle = styled.h3`
  margin-bottom: 15px;
  color: #333;
  font-size: 18px;
  border-bottom: 2px solid #e74c3c;
  padding-bottom: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SelectionIndicator = styled.div`
  background-color: #e74c3c;
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const SelectedItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding: 10px;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #ddd;
`;

const SelectedImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
`;

const SelectedName = styled.span`
  font-weight: bold;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const GenerateButton = styled.button`
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    background-color: #c0392b;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const LoadingMessage = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
`;

const RedesSociaisModal = ({ isOpen, onClose, idRede, fileId }) => {
  const [selectedMoldura, setSelectedMoldura] = useState(null);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [personalizacao, setPersonalizacao] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  useEffect(() => {
    // Remove o preview automático ao digitar
    // O preview só será gerado quando o usuário clicar no botão
  }, [personalizacao, selectedMoldura, selectedLogo, fileId, idRede]);

  const handleSelectMoldura = (moldura) => {
    setSelectedMoldura(moldura);
  };

  const handleSelectLogo = (logo) => {
    setSelectedLogo(logo);
  };

  const handlePersonalizacaoChange = (newPersonalizacao) => {
    setPersonalizacao(newPersonalizacao);
  };

  const handleGenerateImage = async () => {
    if (!selectedMoldura || !selectedLogo || !personalizacao) {
      alert('Por favor, selecione uma moldura, um logo e configure as personalizações.');
      return;
    }

    setIsLoading(true);
    setLoadingMessage('Gerando imagem...');

    try {
      // Monta o objeto com todos os parâmetros necessários
      const personalizacaoCompleta = {
        ...personalizacao,
        moldura_id: selectedMoldura.id,
        nome_moldura: selectedMoldura.nome,
        logo_id: selectedLogo.id,
        posicao_logo: personalizacao.posicao_logo || 'top-right',
        tamanho_logo: personalizacao.tamanho_logo || 15,
        texto: personalizacao.texto || '',
        posicao_texto: personalizacao.posicao_texto || 'bottom',
        cor_texto: personalizacao.cor_texto || '#FFFFFF',
        tamanho_texto: personalizacao.tamanho_texto || 24
      };

      // Gera o preview antes de fazer o download
      const previewResponse = await api.post(
        `${process.env.REACT_APP_API_BASE_URL}/appv12/download/social/${fileId}?id_rede=${idRede}`,
        personalizacaoCompleta
      );
      
      setPreviewUrl(previewResponse.data.url);

      // Faz o download da imagem
      await downloadSocialImage.processarImagem(
        fileId,
        idRede,
        personalizacaoCompleta
      );
      onClose();
    } catch (error) {
      console.error('Erro ao gerar imagem:', error);
      alert('Erro ao gerar imagem. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={e => e.stopPropagation()}>
          <CloseButton onClick={onClose}>
            <MdClose />
          </CloseButton>
          
          <Section>
            <SectionTitle>
              Logos
              {selectedLogo && (
                <SelectionIndicator>
                  <MdCheckCircle />
                  Logo Selecionado
                </SelectionIndicator>
              )}
            </SectionTitle>
            <LogosGallery 
              idRede={idRede}
              onSelectLogo={handleSelectLogo}
              onDeleteLogo={() => {}}
            />
            {selectedLogo && (
              <SelectedItem>
                <SelectedImage src={selectedLogo.thumbnailUrl} alt={selectedLogo.nome} />
                <SelectedName>{selectedLogo.nome}</SelectedName>
              </SelectedItem>
            )}
          </Section>

          <Section>
            <SectionTitle>
              Molduras
              {selectedMoldura && (
                <SelectionIndicator>
                  <MdCheckCircle />
                  Moldura Selecionada
                </SelectionIndicator>
              )}
            </SectionTitle>
            <MoldurasGallery 
              idRede={idRede}
              onSelectMoldura={handleSelectMoldura}
              onDeleteMoldura={() => {}}
            />
            {selectedMoldura && (
              <SelectedItem>
                <SelectedImage src={selectedMoldura.thumbnailUrl} alt={selectedMoldura.nome} />
                <SelectedName>{selectedMoldura.nome}</SelectedName>
              </SelectedItem>
            )}
          </Section>

          <Section>
            <SectionTitle>Personalização</SectionTitle>
            <PersonalizacaoForm
              onPersonalizacaoChange={handlePersonalizacaoChange}
              fileId={fileId}
              idRede={idRede}
              previewUrl={previewUrl}
            />
          </Section>

          <ActionButtons>
            <GenerateButton onClick={handleGenerateImage}>
              Gerar Imagem
            </GenerateButton>
          </ActionButtons>
        </ModalContent>
      </ModalOverlay>

      {isLoading && (
        <LoadingOverlay>
          <LoadingMessage>
            <p>{loadingMessage}</p>
          </LoadingMessage>
        </LoadingOverlay>
      )}
    </>
  );
};

export default RedesSociaisModal; 